// The top bar's contents (design spec 2026-10-19 §1.10, §2): context, not navigation. Left: the
// hamburger (narrow screens), the company switcher and the breadcrumb; then the focus chips, the
// ⌘K search box and the actions (side panel, account; the Inbox is the rail's alone). The recipe is `@companyos/ui`'s
// Topbar; this file only fills it. SIDEBAR-TOGGLE-FIXED: the sidebar's toggle is no longer here;
// it stays at the top of the sidebar, which collapses to a strip.
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  NavToggleButton,
  Topbar,
} from "@companyos/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { Fragment, type ReactElement, type ReactNode } from "react";
import { modeByKey } from "../extensions/modes";
import { setupPageOf } from "../features/settings/setup-catalog";
import { keepFocus } from "../focus/focus";
import { crumbOf, useCachedName } from "./crumbs";
import { useSubjectName } from "./focus-chips";

export type TopbarSlots = {
  companySwitcher: ReactNode;
  /** The mode tabs (company shell): centred in the first row. */
  tabs?: ReactNode;
  breadcrumb?: ReactNode;
  chips?: ReactNode;
  command?: ReactNode;
  /** Phones only: the pinned apps, folded into one menu. */
  mobileApps?: ReactNode;
  /** WOS-BUNDLE: the person's running task timer (a chip; nothing when none runs). */
  timer?: ReactNode;
  /** Side-panel toggle. */
  panel?: ReactNode;
  /** The account menu, when the rail does not carry it (no rail, or a phone). */
  me?: ReactNode;
};

export function TopBar({ slots }: { slots: TopbarSlots }) {
  if (slots.tabs) {
    // Row 1: switcher · the six modes and the gear (centred) · search and actions. Row 2: where
    // am I (the breadcrumb) and the playhead (subject and week chips).
    return (
      <Topbar
        left={
          <>
            <NavToggleButton />
            <div data-slot="topbar-switcher" className="flex min-w-0 shrink items-center">
              {slots.companySwitcher}
            </div>
          </>
        }
        tabs={slots.tabs}
        // INBOX-READABLE: no crumb and no chips (the Inbox page): no second row at all.
        chips={
          !slots.breadcrumb && !slots.chips ? null : (
            <>
              {slots.breadcrumb ? (
                <div
                  data-slot="topbar-breadcrumb"
                  className="flex min-w-0 shrink items-center overflow-hidden pr-1"
                >
                  {slots.breadcrumb}
                </div>
              ) : null}
              <div className="ml-auto flex shrink-0 items-center gap-2">{slots.chips}</div>
            </>
          )
        }
        center={slots.command}
        right={
          <>
            {slots.timer}
            {slots.mobileApps}
            {slots.panel}
            {slots.me}
          </>
        }
      />
    );
  }
  return (
    <Topbar
      left={
        <>
          <NavToggleButton />
          <div data-slot="topbar-switcher" className="flex min-w-0 shrink items-center">
            {slots.companySwitcher}
          </div>
          {slots.breadcrumb ? (
            <div
              data-slot="topbar-breadcrumb"
              className="ml-2 flex min-w-0 shrink items-center overflow-hidden border-l border-border pl-3 max-md:hidden"
            >
              {slots.breadcrumb}
            </div>
          ) : null}
        </>
      }
      chips={slots.chips}
      center={slots.command}
      right={
        <>
          {slots.timer}
          {slots.mobileApps}
          {slots.panel}
          {slots.me}
        </>
      }
    />
  );
}

type Crumb = { label: string; link?: ReactElement };

function Crumbs({ items }: { items: readonly Crumb[] }) {
  if (items.length === 0) return null;
  return (
    <Breadcrumb aria-label="Breadcrumb" className="min-w-0">
      <BreadcrumbList className="min-w-0 flex-nowrap overflow-hidden text-sm">
        {items.map((c, i) => {
          const last = i === items.length - 1;
          return (
            // Middle segments give way first (a higher shrink factor); the page name keeps its room.
            // The separator is the list's own <li> beside the item, never inside it.
            <Fragment key={c.label}>
              <BreadcrumbItem className={last ? "min-w-0 shrink" : "min-w-0 shrink-[4]"}>
                {last || !c.link ? (
                  <BreadcrumbPage className="max-w-60 truncate font-medium text-foreground">
                    {c.label}
                  </BreadcrumbPage>
                ) : (
                  // A touch screen gets the 44 px target (KPIS-MODE: the Scorecard is a page
                  // of KPIs, so its "KPIs" crumb is a link on the phone's chips row).
                  <BreadcrumbLink
                    render={c.link}
                    className="pointer-coarse:inline-flex pointer-coarse:min-h-11 pointer-coarse:min-w-11 pointer-coarse:items-center"
                  >
                    {c.label}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {last ? null : <BreadcrumbSeparator className="shrink-0" />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Where am I inside the company: the mode, then the page (names from loaded data only). Page
 * names come from the crumb registry (crumbs.ts, UX-A), Setup's from the Setup catalog.
 */
export function CompanyBreadcrumb({ slug }: { slug: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [, , , modeKey, ...rest] = pathname.split("/");
  const mode = modeKey ? modeByKey(modeKey) : undefined;
  const deId =
    (modeKey === "kpis" || modeKey === "agents") && rest[0] === "de" ? rest[1] : undefined;
  const deName = useSubjectName(deId ? { kind: "de", id: deId } : null);
  // A seat page (KPIs › Chart) is named from the org chart already loaded.
  const seatId =
    modeKey === "kpis" && rest[0] === "chart" && rest[1] === "seat" ? rest[2] : undefined;
  const seatName = useSubjectName(seatId ? { kind: "seat", id: seatId } : null);
  const crumb = modeKey && modeKey !== "setup" ? crumbOf(modeKey, rest.filter(Boolean)) : null;
  // UX-A: a space, board, channel, meeting… named from data already loaded (none: no crumb).
  const objectName = useCachedName(crumb && "subject" in crumb ? crumb.id : null);
  if (!mode) return null;

  const params = { slug };
  const modeLink = <Link to={mode.path} params={params} search={keepFocus} />;
  const items: Crumb[] = [{ label: mode.label, link: modeLink }];
  // Setup's pages are named by the Setup catalog (SETUP-REGROUP), the sidebar's own list.
  const setupPage = modeKey === "setup" ? setupPageOf(rest.join("/")) : undefined;
  // NAV-REDESIGN: KPIs › Reports › the week or the Finance page.
  const reportsPage = modeKey === "kpis" && rest[0] === "reports";
  if (reportsPage) {
    items.push({
      label: "Reports",
      link: <Link to="/c/$slug/kpis/reports" params={params} search={keepFocus} />,
    });
    const page = rest.slice(1).filter(Boolean);
    if (page[0] && page[0] !== "finance")
      items.push({ label: page[0] === "live" ? "Live" : page[0] });
    else if (crumb && "label" in crumb) items.push({ label: crumb.label });
  } else if (deId) items.push({ label: deName });
  else if (seatId) items.push({ label: seatName });
  else if (setupPage) items.push({ label: setupPage.label });
  else if (modeKey === "agents" && !rest.some(Boolean)) items.push({ label: "Digital employees" });
  else if (crumb && "label" in crumb) items.push({ label: crumb.label });
  else if (objectName) items.push({ label: objectName });
  return <Crumbs items={items} />;
}

const OPERATOR_PAGES: Record<string, string> = {
  // SUPER-ADMIN
  "/operator/clients": "Clients",
  // ONBOARDING-P3
  "/operator/onboarding": "Onboarding",
  // SKILLSMITH-CONTROL
  "/operator/skillsmith": "Skillsmith",
  "/operator/gate": "Gate tracker",
  "/operator/consumers": "Failed events",
  "/operator/des": "Digital employees",
  "/operator/system-agents": "System agents",
  // Before Agent health: the first prefix that matches names the page.
  "/operator/agent-health/opportunities": "Opportunities",
  "/operator/agent-health": "Agent health",
  "/operator/memory-quality": "Memory quality",
  "/operator/templates": "Templates",
  "/operator/deploys": "Deploys",
  // M5-05
  "/operator/approvals": "Approvals SLA",
  "/operator/supervisors": "Supervisor load",
  "/operator/cost": "Cost to serve",
  "/operator/break-glass": "Break-glass",
  // OPERATOR-CONSOLE
  "/operator/settings": "Settings",
};

export function OperatorBreadcrumb() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The switcher already says "Operator console"; the crumb names the page.
  const page = Object.entries(OPERATOR_PAGES).find(([p]) => pathname.startsWith(p))?.[1];
  return <Crumbs items={[{ label: page ?? "Companies" }]} />;
}
